/**
 * KeyPasswordForm — sekcja "Hasło do klucza" z osobnymi buttonami Zapisz/Usuń.
 *
 * Część split-upload UI (refaktor 2026-06-01).
 *
 * Po zapisaniu hasła backend testuje czy odblokuje klucz (silent failure
 * prevention) — jeśli mismatch, pokazujemy ostrzeżenie.
 *
 * Props:
 *   - adminConfig (object)  — bieżący stan z /admin/config
 *   - onSaved (callback)    — wywołane po pomyślnym zapisie/usunięciu
 */
import { useState } from "react";
import { logger } from "../../../utils/logger";
import { toast } from "sonner";
import { Save, Lock, CheckCircle, Trash2, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { saveKeyPassword, deleteKeyPassword } from "../services/ksefApi";
import { safeDetailMessage, safeErrorMessage } from "../utils/errorMessages";

export const KeyPasswordForm = ({ adminConfig, onSaved }) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pairWarning, setPairWarning] = useState(null);

  const hasPassword = !!adminConfig?.has_key_password;

  const handleSave = async () => {
    if (!password || password.length < 4) {
      toast.error("Hasło musi mieć co najmniej 4 znaki");
      return;
    }
    setSaving(true);
    setPairWarning(null);
    try {
      const response = await saveKeyPassword(password);
      const data = response.data || {};
      if (data.pair_check === "OK") {
        toast.success("Hasło zapisane — klucz odblokowany prawidłowo");
        setPairWarning(null);
      } else if (data.pair_check === "MISMATCH") {
        toast.warning("Hasło zapisane, ale nie pasuje do klucza");
        setPairWarning(data.pair_warning || "Hasło nie pasuje do zapisanego klucza");
      } else {
        toast.success("Hasło zapisane");
        setPairWarning(data.pair_warning || null);
      }
      setPassword("");
      if (onSaved) onSaved();
    } catch (err) {
      logger.error("Error saving password:", err);
      toast.error(
        safeDetailMessage(err.response) || safeErrorMessage(err) || "Błąd zapisywania hasła",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteKeyPassword();
      toast.success("Hasło usunięte");
      setDeleteDialogOpen(false);
      setPairWarning(null);
      if (onSaved) onSaved();
    } catch (err) {
      logger.error("Error deleting password:", err);
      toast.error(
        safeDetailMessage(err.response) || safeErrorMessage(err) || "Błąd usuwania hasła",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="border border-gray-200 rounded-lg p-4 space-y-3"
      data-testid="key-password-section"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
            <Lock className="w-4 h-4 text-gray-500" />
            Hasło do klucza prywatnego <span className="text-red-500">*</span>
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Wymagane (min 4 znaki). Hasło jest szyfrowane AES-256 przed zapisem.
            Bez hasła nie da się pobrać faktur z KSeF.
          </p>
        </div>
        {hasPassword && (
          <div className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded whitespace-nowrap">
            <CheckCircle className="w-3 h-3" />
            Zapisane
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="key-password" className="text-xs">
          {hasPassword ? "Wpisz nowe hasło aby je zmienić" : "Podaj hasło do klucza"}
        </Label>
        <div className="relative">
          <Input
            id="key-password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="pr-10 font-mono"
            data-testid="key-password-input"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            data-testid="key-password-toggle"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {pairWarning && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs text-amber-900">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{pairWarning}</span>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          onClick={handleSave}
          disabled={!password || password.length < 4 || saving}
          className="flex-1"
          data-testid="key-password-save-btn"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Zapisz hasło
        </Button>

        {hasPassword && (
          <Button
            variant="outline"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={saving || deleting}
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid="key-password-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Usuń
          </Button>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="key-password-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Usunąć zapisane hasło do klucza?</AlertDialogTitle>
            <AlertDialogDescription>
              Po usunięciu hasła trzeba będzie wprowadzić je ponownie przed kolejnym
              testem certyfikatu lub synchronizacją z KSeF.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="key-password-delete-cancel-btn">
              Anuluj
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              data-testid="key-password-delete-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? "Usuwam..." : "Tak, usuń hasło"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default KeyPasswordForm;
